import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Star, Check, X, Trash2 } from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

export default function Reviews() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => { const r = await api.get("/reviews"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const approve = async (id, approved) => {
    await api.put(`/reviews/${id}/approve?approved=${approved}`);
    toast.success(lang === "ar" ? "تم التحديث" : "Updated");
    load();
  };
  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف التقييم؟" : "Delete review?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/reviews/${id}`); load();
  };

  const filtered = items.filter((r) => filter === "all" || (filter === "approved" && r.approved) || (filter === "pending" && !r.approved));

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "تقييمات العملاء" : "Customer Reviews"}</h1>
        <div className="flex gap-2 text-sm">
          {[{ id: "all", l: lang === "ar" ? "الكل" : "All" }, { id: "pending", l: lang === "ar" ? "قيد المراجعة" : "Pending" }, { id: "approved", l: lang === "ar" ? "موافق عليها" : "Approved" }].map((b) => (
            <button key={b.id} onClick={() => setFilter(b.id)} className={`px-3 py-1.5 rounded-md ${filter === b.id ? "bg-[var(--gold)] text-white" : "border border-[var(--border)]"}`}>{b.l}</button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => (
          <div key={r.review_id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-[var(--text)]">{r.customer_name}</div>
                <div className="text-xs text-[var(--text-muted)]">{r.customer_phone || ""}</div>
              </div>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} fill={n <= r.rating ? "#9B2950" : "none"} color="#9B2950" />)}
              </div>
            </div>
            <p className="text-sm text-[var(--text)] mt-3 italic">"{r.comment}"</p>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--border)]">
              <span className={`text-xs px-2 py-0.5 rounded ${r.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r.approved ? (lang === "ar" ? "موافق عليه" : "Approved") : (lang === "ar" ? "قيد المراجعة" : "Pending")}</span>
              <div className="flex gap-1">
                {!r.approved && <button data-testid={`approve-review-${r.review_id}`} onClick={() => approve(r.review_id, true)} className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200"><Check size={14} /></button>}
                {r.approved && <button onClick={() => approve(r.review_id, false)} className="p-1.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200"><X size={14} /></button>}
                <button onClick={() => remove(r.review_id)} className="p-1.5 rounded bg-rose-50 text-rose-500 hover:bg-rose-100"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="md:col-span-3 text-center text-[var(--text-muted)] py-12">{lang === "ar" ? "لا توجد تقييمات" : "No reviews"}</div>}
      </div>
      <ConfirmDialogSlot />
    </div>
  );
}
